import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, DataTable, EmptyState, Input, KpiTile, Select, Spinner } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { itemPath } from '../../lib/slug';
import { catalogApi } from './catalogApi';

// Period presets, expressed in days back from today. "custom" reveals explicit date pickers.
const PERIODS = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 6 months' },
  { value: '365', label: 'Last 12 months' },
  { value: 'custom', label: 'Custom range…' },
];

const DIRECTIONS = [
  { value: 'all', label: 'All movements' },
  { value: 'up', label: 'Increases only' },
  { value: 'down', label: 'Decreases only' },
];

const isoDate = (d) => d.toISOString().slice(0, 10);
const money = (amount, currency) => `${currency} ${formatAmount(amount)}`;
const fmtPct = (pct) => (pct === null || pct === undefined ? '—' : `${pct > 0 ? '+' : ''}${Number(pct).toFixed(1)}%`);

// Procurement convention: a price going up is unwelcome (danger), going down is a saving (success).
function MovementCell({ row }) {
  const t = useT();
  const change = row.changeAmount || 0;
  if (change > 0) {
    return (
      <Badge tone="danger" className="pm-change">
        <Icon.TrendingUp size={13} />+{money(change, row.currency)}
      </Badge>
    );
  }
  if (change < 0) {
    return (
      <Badge tone="success" className="pm-change">
        <Icon.TrendingDown size={13} />{money(change, row.currency)}
      </Badge>
    );
  }
  return (
    <Badge tone="neutral" className="pm-change">
      <Icon.Minus size={13} />{t('No change')}
    </Badge>
  );
}

export default function PriceMovementsTab({ categories = [], canManage }) {
  const navigate = useNavigate();
  const t = useT();

  const [period, setPeriod] = useState('90');
  const [customFrom, setCustomFrom] = useState(isoDate(new Date(Date.now() - 90 * 86400000)));
  const [customTo, setCustomTo] = useState(isoDate(new Date()));
  const [direction, setDirection] = useState('all');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const catName = (id) => categories.find((c) => c.id === id)?.name;

  const range = useMemo(() => {
    if (period === 'custom') return { from: customFrom, to: customTo };
    const days = Number(period);
    return { from: isoDate(new Date(Date.now() - days * 86400000)), to: isoDate(new Date()) };
  }, [period, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await catalogApi.priceMovements({ from: range.from, to: range.to }));
    } catch (err) {
      setError(err?.message || t('Failed to load price movements.'));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, t]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    if (direction === 'up') return rows.filter((r) => (r.changeAmount || 0) > 0);
    if (direction === 'down') return rows.filter((r) => (r.changeAmount || 0) < 0);
    return rows;
  }, [rows, direction]);

  const kpis = useMemo(() => {
    const up = rows.filter((r) => (r.changeAmount || 0) > 0);
    const down = rows.filter((r) => (r.changeAmount || 0) < 0);
    const biggest = rows.reduce(
      (best, r) => (Math.abs(r.changePercent || 0) > Math.abs(best?.changePercent || 0) ? r : best),
      null,
    );
    return { tracked: rows.length, up: up.length, down: down.length, biggest };
  }, [rows]);

  const columns = [
    {
      key: 'item', header: t('Item'), width: 260,
      render: (r) => (
        <div className="pm-item">
          <span className="pm-item__name">{r.itemName}</span>
          <span className="pm-item__meta">{[r.sku, catName(r.categoryId)].filter(Boolean).join(' · ') || '—'}</span>
        </div>
      ),
    },
    { key: 'first', header: t('First'), width: 120, align: 'right', render: (r) => money(r.firstPrice, r.currency) },
    { key: 'last', header: t('Latest'), width: 120, align: 'right', render: (r) => money(r.lastPrice, r.currency) },
    { key: 'change', header: t('Change'), width: 160, render: (r) => <MovementCell row={r} /> },
    {
      key: 'pct', header: '%', width: 90, align: 'right',
      render: (r) => {
        const c = r.changeAmount || 0;
        const tone = c > 0 ? 'pm-pct--up' : c < 0 ? 'pm-pct--down' : '';
        return <span className={`pm-pct ${tone}`}>{fmtPct(r.changePercent)}</span>;
      },
    },
    { key: 'range', header: t('Range (min–max)'), width: 180, align: 'right', render: (r) => `${money(r.minPrice, r.currency)} – ${money(r.maxPrice, r.currency)}` },
    { key: 'count', header: t('Purchases'), width: 100, align: 'right', render: (r) => r.purchaseCount },
    { key: 'lastDate', header: t('Last purchase'), width: 130, render: (r) => new Date(r.lastDate).toLocaleDateString() },
  ];

  return (
    <>
      <div className="item-kpis">
        <KpiTile label={t('Items with movement')} value={kpis.tracked} />
        <KpiTile label={t('Price increases')} value={kpis.up} meta={t('went up')} />
        <KpiTile label={t('Price decreases')} value={kpis.down} meta={t('came down')} />
        <KpiTile
          label={t('Biggest mover')}
          value={kpis.biggest ? fmtPct(kpis.biggest.changePercent) : '—'}
          meta={kpis.biggest?.itemName}
        />
      </div>

      <div className="pp-toolbar">
        <div className="ps-field">
          <span className="ps-field__label">{t('Period')}</span>
          <Select value={period} onChange={setPeriod} options={PERIODS.map((p) => ({ ...p, label: t(p.label) }))} ariaLabel={t('Period')} />
        </div>
        {period === 'custom' && (
          <>
            <Input label={t('From')} type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            <Input label={t('To')} type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </>
        )}
        <div className="ps-field">
          <span className="ps-field__label">{t('Show')}</span>
          <Select value={direction} onChange={setDirection} options={DIRECTIONS.map((d) => ({ ...d, label: t(d.label) }))} ariaLabel={t('Movement direction')} />
        </div>
        <div className="pp-toolbar__spacer" />
        <Button variant="ghost" leadingIcon={<Icon.Refresh size={16} />} onClick={load}>{t('Refresh')}</Button>
      </div>

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load price movements")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}>
          <Spinner size={26} />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<Icon.TrendingUp size={28} />}
          title={t('No price movements in this period')}
          description={
            canManage
              ? t('Record purchases against your items (Catalog → item → Purchase history) to track how their prices move over time.')
              : t('No purchases with prices were recorded in the selected period.')
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={visible}
          rowKey={(r) => r.itemId}
          density="comfortable"
          onRowClick={(r) => navigate(itemPath({ id: r.itemId, name: r.itemName }))}
        />
      )}
    </>
  );
}
